import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useAgentContext } from '@Providers';

import { GuidedJourney } from '@Models';
import useGreeting from '../../../CustomHooks/Agents/useGreeting';
import useIsMobile from '../../../CustomHooks/useIsMobile';
import { AGENT_DURATIONS, AGENT_EASING } from '../../../Helpers/Agents/animations';
import JourneyGrid from './JourneyGrid';
import PersonalizeCard from './PersonalizeCard';
import './Welcome.css';

interface WelcomeProps {
  preferredName?: string | null;
  journeys: GuidedJourney[];
  isLoadingJourneys?: boolean;
  isPersonalizeVisible?: boolean;
  onPersonalize?: () => void;
  onSelectJourney?: (journey: GuidedJourney) => void;
}

const BetaBadge = () => (
  <span
    className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
    style={{ backgroundColor: 'rgba(124, 108, 246, 0.12)', color: '#5B4BD6' }}>
    BETA
  </span>
);

const Welcome = ({
  preferredName,
  journeys,
  isLoadingJourneys = false,
  isPersonalizeVisible = false,
  onPersonalize,
  onSelectJourney,
}: WelcomeProps) => {
  const { mode } = useAgentContext();
  const { t } = useTranslation();
  const { hello, prompt, subtitle, hasName } = useGreeting({ preferredName });
  const isMobile = useIsMobile();

  // On phones the agent is fullscreen but adopts the compact, centered chrome.
  const isFullscreen = mode === 'fullscreen' && !isMobile;
  const helloClass = hasName ? 'agent-greeting' : 'text-gray-900';
  const showJourneysSection = isLoadingJourneys || journeys.length > 0;

  if (!isFullscreen) {
    // Compact: greeting line + BETA badge only; the call-to-action is fullscreen-only.
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: AGENT_DURATIONS.slow, ease: AGENT_EASING.outExpo }}
        className="flex h-fit flex-col items-center justify-center px-7 py-6 text-center">
        <div className="mb-1.5 flex items-center gap-3">
          <h3 className={`text-lg font-semibold leading-tight ${helloClass}`}>{hello.replace(/\.$/, '')}</h3>
          <BetaBadge />
        </div>
        <p className="mb-5 max-w-sm text-sm leading-relaxed text-gray-500">{subtitle}</p>
        <div className="w-full max-w-md space-y-3">
          {isPersonalizeVisible && (
            <div className="mb-4">
              <PersonalizeCard onClick={onPersonalize} />
            </div>
          )}
          {showJourneysSection && (
            <>
              <p className="text-left text-xs font-medium uppercase tracking-wider text-gray-400">
                {t('Agent.GuidedJourneysLabel')}
              </p>
              <JourneyGrid journeys={journeys} isLoading={isLoadingJourneys} onSelect={onSelectJourney} />
            </>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: AGENT_DURATIONS.slow, ease: AGENT_EASING.outExpo }}
      className="mx-auto flex w-full min-w-0 max-w-3xl flex-col px-5 pt-6 pb-5 sm:pt-10">
      <div className="text-left">
        <h1 className={`text-[clamp(24px,3vw,32px)] font-semibold leading-[1.15] tracking-tight ${helloClass}`}>
          {hello}
        </h1>
        <h2 className="text-[clamp(24px,3vw,32px)] font-semibold leading-[1.15] tracking-tight text-gray-900">
          {prompt}
        </h2>
        <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-gray-500">{subtitle}</p>
      </div>

      {isPersonalizeVisible && (
        <div className="mt-5">
          <PersonalizeCard onClick={onPersonalize} />
        </div>
      )}

      {showJourneysSection && (
        <>
          <p className="mt-5 mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {t('Agent.GuidedJourneysLabel')}
          </p>
          <JourneyGrid journeys={journeys} isLoading={isLoadingJourneys} onSelect={onSelectJourney} />
        </>
      )}
    </motion.div>
  );
};

export default Welcome;
